import OrderSpecificationReview from '../components/OrderSpecificationReview';
import React, { useState, useEffect } from 'react';

import OpenProductionTasksWarning from '../components/OpenProductionTasksWarning';
import { get, post, put } from '../utils/api';
import MaterialIssuance, { JobCostingSummary } from '../components/JobCosting';
import { useAuth } from '../context/AuthContext';

const STATUSES = [
  { key:'pending',       label:'Pending',        color:'#8A8A8A', bg:'#F0F0F0' },
  { key:'confirmed',     label:'Completed',      color:'#1E4FAD', bg:'#EDF4FF' },
  { key:'in_production', label:'In Production',  color:'#8B5E0A', bg:'#FFF3DC' },
  { key:'printing',      label:'Printing',       color:'#6B2FA0', bg:'#F3EAFF' },
  { key:'quality_check', label:'Quality Check',  color:'#0A6B5E', bg:'#E0FFF8' },
  { key:'ready',         label:'Ready',          color:'#2E6B3A', bg:'#E8F5EB' },
  { key:'dispatched',    label:'Dispatched',     color:'#556B2F', bg:'#F2EFE7' },
  { key:'cancelled',     label:'Cancelled',      color:'#8F2E24', bg:'#FDEAEA' },
];

const getStatus = key => STATUSES.find(s => s.key === key) || STATUSES[0];

const StatusBadge = ({ status }) => {
  const s = getStatus(status);
  return (
    <span style={{display:'inline-flex',padding:'3px 10px',borderRadius:20,
      fontSize:10,fontWeight:600,textTransform:'uppercase',letterSpacing:'.05em',
      background:s.bg,color:s.color}}>
      {s.label}
    </span>
  );
};

function SecureMockupImage({
  jobId,
  mockup
}) {
  const [source, setSource] =
    useState('');
  const [failed, setFailed] =
    useState(false);

  useEffect(() => {
    const controller =
      new AbortController();

    let objectUrl = '';

    const loadImage = async () => {
      try {
        const token =
          localStorage.getItem(
            'bb_token'
          );

        const response = await fetch(
          '/api/job-orders/' +
            jobId +
            '/mockups/' +
            mockup.id +
            '/file',
          {
            headers: {
              Authorization:
                'Bearer ' + token
            },
            signal: controller.signal
          }
        );

        if (!response.ok) {
          throw new Error(
            'Mockup could not be loaded'
          );
        }

        const contentType =
          response.headers.get(
            'content-type'
          ) || '';

        if (
          !contentType.startsWith(
            'image/'
          )
        ) {
          throw new Error(
            'Mockup response is not an image'
          );
        }

        const blob =
          await response.blob();

        objectUrl =
          URL.createObjectURL(blob);

        setSource(objectUrl);
      } catch (error) {
        if (
          error.name !==
          'AbortError'
        ) {
          setFailed(true);
        }
      }
    };

    loadImage();

    return () => {
      controller.abort();

      if (objectUrl) {
        URL.revokeObjectURL(
          objectUrl
        );
      }
    };
  }, [
    jobId,
    mockup.id
  ]);

  if (failed) {
    return (
      <div
        style={{
          width:'100%',
          height:120,
          display:'flex',
          alignItems:'center',
          justifyContent:'center',
          background:'#FDEAEA',
          color:'#8F2E24',
          fontSize:11,
          textAlign:'center',
          padding:12
        }}
      >
        Image could not be loaded
      </div>
    );
  }

  if (!source) {
    return (
      <div
        style={{
          width:'100%',
          height:120,
          display:'flex',
          alignItems:'center',
          justifyContent:'center',
          background:'#F2EFE7'
        }}
      >
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <img
      src={source}
      alt={
        mockup.original_name ||
        'Job Order mockup'
      }
      style={{
        width:'100%',
        height:120,
        objectFit:'cover',
        display:'block'
      }}
    />
  );
}

export default function JobOrders() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingOverrides, setPendingOverrides] = useState([]);
  const [showOverrides, setShowOverrides] = useState(false);
  const [filter, setFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [viewJob, setViewJob] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);

  const userPermissions =
    Array.isArray(user?.permissions)
      ? user.permissions
      : [];

  const canCreate =
    ['super_admin','sales_manager'].includes(
      user?.role
    ) ||
    userPermissions.includes(
      'invoices:write'
    ) ||
    user?.role ===
      'field_sales_representative';

  const load = () => {
    setLoading(true);
    const qs = filter ? '?status='+filter : '';
    get('/job-orders'+qs).then(d => setJobs(d.data||[])).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    get('/invoices').then(d => setInvoices(d.data||[]));
    get('/customers').then(d => setCustomers(d.data||[]));
  }, [filter]);

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">Job Orders</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>Production job cards for the factory team</p>
        </div>
        {canCreate && (
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>+ New Job Order</button>
        )}
      </div>
      <div className="search-bar" style={{marginBottom:16,flexWrap:'wrap'}}>
        <button className={'btn btn-sm '+(filter===''?'btn-primary':'btn-outline')} onClick={() => setFilter('')}>All</button>
        {STATUSES.map(s => (
          <button key={s.key} className={'btn btn-sm '+(filter===s.key?'btn-primary':'btn-outline')}
            onClick={() => setFilter(s.key)} style={filter===s.key?{}:{color:s.color,borderColor:s.color}}>
            {s.label}
          </button>
        ))}
      </div>
      {loading ? <div className="spinner"></div> : (
        jobs.length === 0
          ? <div className="empty-state"><h3>No job orders yet</h3><p>Create a job order from a confirmed invoice.</p></div>
          : <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))',gap:16}}>
              {jobs.map(job => (
                <div key={job.id} style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,boxShadow:'var(--shadow)',overflow:'hidden',cursor:'pointer'}} onClick={() => setViewJob(job)}>
                  <div style={{background:'#556B2F',padding:'12px 16px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <div>
                      <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.1rem',color:'#A8893D',fontWeight:600}}>{job.job_number}</div>
                      <div style={{fontSize:11,color:'rgba(242,239,231,.6)',marginTop:2}}>{job.invoice_number}</div>
                    </div>
                    <StatusBadge status={job.status} />
                  </div>
                  <div style={{padding:'14px 16px'}}>
                    <div style={{fontWeight:600,fontSize:14,color:'#3B2A21',marginBottom:8}}>{job.customer_name}</div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,fontSize:12,color:'#555'}}>
                      {job.product_description && <div><span style={{color:'#8A8A8A'}}>Product: </span>{job.product_description}</div>}
                      {job.size && <div><span style={{color:'#8A8A8A'}}>Size: </span>{job.size}</div>}
                      {job.quantity && <div><span style={{color:'#8A8A8A'}}>Qty: </span>{Number(job.quantity).toLocaleString()}</div>}
                      {job.color && <div><span style={{color:'#8A8A8A'}}>Color: </span>{job.color}</div>}
                      {job.material && <div><span style={{color:'#8A8A8A'}}>Material: </span>{job.material}</div>}
                      {job.handle_type && <div><span style={{color:'#8A8A8A'}}>Handle: </span>{job.handle_type}</div>}
                    </div>
                    {job.print_description && (
                      <div style={{marginTop:8,padding:'8px 10px',background:'#F2EFE7',borderRadius:6,fontSize:12,color:'#3B2A21'}}>
                        <span style={{color:'#8A8A8A',fontWeight:600}}>Print: </span>{job.print_description}
                      </div>
                    )}
                    {job.special_instructions && (
                      <div style={{marginTop:6,padding:'8px 10px',background:'#FFF3DC',borderRadius:6,fontSize:12,color:'#3B2A21'}}>
                        <span style={{color:'#8B5E0A',fontWeight:600}}>Special: </span>{job.special_instructions}
                      </div>
                    )}
                    <div style={{marginTop:10,display:'flex',justifyContent:'space-between',fontSize:11,color:'#8A8A8A'}}>
                      <span>Created: {job.date}</span>
                      {job.delivery_date && <span style={{color:'#8F2E24',fontWeight:600}}>Due: {job.delivery_date}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
      )}
      {showCreate && <CreateJobModal invoices={invoices} customers={customers} onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); load(); }} />}
      {viewJob && <ViewJobModal jobId={viewJob.id} onClose={() => setViewJob(null)} onUpdated={() => { setViewJob(null); load(); }} />}
    </div>
  );
}

function CreateJobModal({ invoices, customers, onClose, onSaved }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    invoice_id:'', customer_id:'',
    date: new Date().toISOString().split('T')[0],
    delivery_date:'', product_description:'', size:'',
    quantity:'', material:'', color:'', print_description:'',
    handle_type:'', special_instructions:'', notes:'',
    customer_phone:'', customer_email:'', customer_address:'', delivery_instructions:''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const canSeeContact = ['super_admin','admin','sales_manager','content_sales'].includes(user?.role);

  const handleInvoiceChange = async (invoice_id) => {
    const inv = invoices.find(i => i.id === invoice_id);
    setForm(previous=>({...previous,invoice_id,customer_id:inv?.customer_id||'',product_description:'',quantity:'',size:''}));
    if(!invoice_id)return;
    try {
      const detail=await get('/invoices/'+invoice_id);
      const items=detail.lines||[];
      setForm(previous=>previous.invoice_id!==invoice_id?previous:{...previous,
        product_description:items.map(l=>l.quantity+' × '+l.description).join('\n'),
        quantity:items.length===1?Number(items[0].quantity):'',
        size:[...new Set(items.map(l=>l.size).filter(Boolean))].join('; ')});
    } catch(e) {setError(e.message);}
  };

  const submit = async () => {
    if (!form.invoice_id) { setError('Please select an invoice'); return; }
    if (!form.date) { setError('Date required'); return; }
    try {
      const existing = await get('/job-orders?invoice_id='+form.invoice_id);
      const active = (existing.data||[]).filter(j => j.status !== 'cancelled');
      if (active.length > 0) {
        const ok = window.confirm('This invoice already has '+active.length+' active job order(s). Add another for a different product?');
        if (!ok) return;
      }
    } catch {}
    setSaving(true);
    try {
      await post('/job-orders', form);
      onSaved();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:680}}>
        <div className="modal-hdr">
          <span className="modal-title">New Job Order</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error mb-md">{error}</div>}
          <div style={{background:'#F2EFE7',borderRadius:8,padding:16,marginBottom:16}}>
            <div style={{fontSize:10,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:10}}>Order Reference</div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Linked Invoice *</label>
                <select className="form-control" value={form.invoice_id} onChange={e => handleInvoiceChange(e.target.value)}>
                  <option value="">Select invoice</option>
                  {invoices.filter(i => i.status !== 'cancelled').map(i => (
                    <option key={i.id} value={i.id}>{i.invoice_number} - {i.customer_name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Job Date *</label>
                <input type="date" className="form-control" value={form.date} onChange={e => setForm({...form, date:e.target.value})} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Delivery / Due Date</label>
              <input type="date" className="form-control" value={form.delivery_date} onChange={e => setForm({...form, delivery_date:e.target.value})} />
            </div>
          </div>
          <div style={{marginBottom:16}}>
            <div style={{fontSize:10,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:10}}>Product Specifications</div>
            <div className="form-group">
              <label className="form-label">Product Description</label>
              <input className="form-control" placeholder="e.g. Jute Tote Bag, Gift Box, Ribbon Bag" value={form.product_description} onChange={e => setForm({...form, product_description:e.target.value})} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Size</label>
                <input className="form-control" placeholder="e.g. 30x40cm, A4, Medium" value={form.size} onChange={e => setForm({...form, size:e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">Quantity</label>
                <input type="number" className="form-control" value={form.quantity} onChange={e => setForm({...form, quantity:e.target.value})} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Material</label>
                <input className="form-control" placeholder="e.g. Natural Jute, Brown Kraft" value={form.material} onChange={e => setForm({...form, material:e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">Color</label>
                <input className="form-control" placeholder="e.g. Natural, Black, Cream" value={form.color} onChange={e => setForm({...form, color:e.target.value})} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Handle Type</label>
              <input className="form-control" placeholder="e.g. Rope handles, Cotton ribbon, No handle" value={form.handle_type} onChange={e => setForm({...form, handle_type:e.target.value})} />
            {canSeeContact && (
              <div style={{background:'#FFF3DC',border:'1px solid #F5C842',borderRadius:8,padding:16,marginTop:12}}>
                <div style={{fontSize:10,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',
                  color:'#8B5E0A',marginBottom:10}}>🔒 Customer Contact & Delivery (Restricted)</div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Customer Phone</label>
                    <input className="form-control" value={form.customer_phone||''}
                      onChange={e=>setForm({...form,customer_phone:e.target.value})}
                      placeholder="Customer phone number" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Customer Email</label>
                    <input className="form-control" value={form.customer_email||''}
                      onChange={e=>setForm({...form,customer_email:e.target.value})}
                      placeholder="Customer email" />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Delivery Address</label>
                  <textarea className="form-control" rows={2} value={form.customer_address||''}
                    onChange={e=>setForm({...form,customer_address:e.target.value})}
                    placeholder="Full delivery address" />
                </div>
                <div className="form-group">
                  <label className="form-label">Delivery Instructions</label>
                  <textarea className="form-control" rows={2} value={form.delivery_instructions||''}
                    onChange={e=>setForm({...form,delivery_instructions:e.target.value})}
                    placeholder="Special delivery instructions, landmarks, contact person at delivery location" />
                </div>
              </div>
            )}
            </div>
          </div>
          <div>
            <div style={{fontSize:10,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:10}}>Print and Instructions</div>
            <div className="form-group">
              <label className="form-label">Print Description</label>
              <textarea className="form-control" rows={3} placeholder="Describe the print - logo position, text, colors, artwork reference..." value={form.print_description} onChange={e => setForm({...form, print_description:e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">Special Instructions</label>
              <textarea className="form-control" rows={2} placeholder="Any special requirements for production..." value={form.special_instructions} onChange={e => setForm({...form, special_instructions:e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">Internal Notes</label>
              <textarea className="form-control" rows={2} value={form.notes} onChange={e => setForm({...form, notes:e.target.value})} />
            </div>
          </div>
        </div>
        <div className="modal-ftr">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>{saving ? 'Creating...' : 'Create Job Order'}</button>
        </div>
      </div>
    </div>
  );
}

function EditJobModal({ job, onClose, onSaved }) {
  const [form, setForm] = React.useState({
    delivery_date: job.delivery_date||'', product_description: job.product_description||'',
    size: job.size||'', quantity: job.quantity||'', material: job.material||'',
    color: job.color||'', print_description: job.print_description||'',
    handle_type: job.handle_type||'', special_instructions: job.special_instructions||'',
    notes: job.notes||'',
    customer_phone: job.customer_phone||'', customer_email: job.customer_email||'',
    customer_address: job.customer_address||'', delivery_instructions: job.delivery_instructions||''
  });
  const { user } = useAuth();
  const canSeeContact = ['super_admin','admin','sales_manager','content_sales'].includes(user?.role);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');

  const submit = async () => {
    setSaving(true);
    try { await put('/job-orders/'+job.id, form); onSaved(); }
    catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" style={{zIndex:300}}>
      <div className="modal" style={{maxWidth:600}}>
        <div className="modal-hdr">
          <span className="modal-title">Edit - {job.job_number}</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error mb-md">{error}</div>}
          <div className="form-group"><label className="form-label">Delivery Date</label>
            <input type="date" className="form-control" value={form.delivery_date} onChange={e => setForm({...form, delivery_date:e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Product Description</label>
            <input className="form-control" value={form.product_description} onChange={e => setForm({...form, product_description:e.target.value})} /></div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Size</label>
              <input className="form-control" value={form.size} onChange={e => setForm({...form, size:e.target.value})} /></div>
            <div className="form-group"><label className="form-label">Quantity</label>
              <input type="number" className="form-control" value={form.quantity} onChange={e => setForm({...form, quantity:e.target.value})} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Material</label>
              <input className="form-control" value={form.material} onChange={e => setForm({...form, material:e.target.value})} /></div>
            <div className="form-group"><label className="form-label">Color</label>
              <input className="form-control" value={form.color} onChange={e => setForm({...form, color:e.target.value})} /></div>
          </div>
          <div className="form-group"><label className="form-label">Handle Type</label>
            <input className="form-control" value={form.handle_type} onChange={e => setForm({...form, handle_type:e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Print Description</label>
            <textarea className="form-control" rows={3} value={form.print_description} onChange={e => setForm({...form, print_description:e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Special Instructions</label>
            <textarea className="form-control" rows={2} value={form.special_instructions} onChange={e => setForm({...form, special_instructions:e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Internal Notes</label>
            <textarea className="form-control" rows={2} value={form.notes} onChange={e => setForm({...form, notes:e.target.value})} /></div>
          {canSeeContact && (
            <div style={{background:'#FFF3DC',border:'1px solid #F5C842',borderRadius:8,padding:16,marginTop:8}}>
              <div style={{fontSize:10,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',
                color:'#8B5E0A',marginBottom:10}}>🔒 Customer Contact & Delivery (Restricted)</div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Customer Phone</label>
                  <input className="form-control" value={form.customer_phone}
                    onChange={e=>setForm({...form,customer_phone:e.target.value})}
                    placeholder="Customer phone number" />
                </div>
                <div className="form-group">
                  <label className="form-label">Customer Email</label>
                  <input className="form-control" value={form.customer_email}
                    onChange={e=>setForm({...form,customer_email:e.target.value})}
                    placeholder="Customer email" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Delivery Address</label>
                <textarea className="form-control" rows={2} value={form.customer_address}
                  onChange={e=>setForm({...form,customer_address:e.target.value})}
                  placeholder="Full delivery address" />
              </div>
              <div className="form-group">
                <label className="form-label">Delivery Instructions</label>
                <textarea className="form-control" rows={2} value={form.delivery_instructions}
                  onChange={e=>setForm({...form,delivery_instructions:e.target.value})}
                  placeholder="Special delivery instructions, landmarks, contact person" />
              </div>
            </div>
          )}
        </div>
        <div className="modal-ftr">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </div>
    </div>
  );
}

function ViewJobModal({ jobId, onClose, onUpdated }) {
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [mockups, setMockups] = useState([]);

  // MISSING MOCKUP REQUEST INTERFACE
  const [
    mockupRequestState,
    setMockupRequestState
  ] = useState(null);

  const [
    requestingMockup,
    setRequestingMockup
  ] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [showDispatch, setShowDispatch] = useState(false);
  const [showDeliver, setShowDeliver] = useState(false);
  const [dispatchForm, setDispatchForm] = useState({ logistics_company:'', driver_name:'', driver_phone:'', tracking_number:'', delivery_address:'', dispatch_notes:'', expected_delivery:'', balance_override_reason:'' });
  const [deliverForm, setDeliverForm] = useState({ actual_delivery_date: new Date().toISOString().split('T')[0], delivery_notes:'' });
  const [dispatchError, setDispatchError] = useState('');
  const [balanceWarning, setBalanceWarning] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const canDispatch = ['super_admin','admin','sales_manager','inventory_officer'].includes(user?.role);

  const canRequestMockup = [
    'super_admin',
    'production_team_lead'
  ].includes(user?.role);

  const handleDispatch = async (overrideReason=null) => {
    setSubmitting(true); setDispatchError('');
    try {
      const payload = { ...dispatchForm };
      if (overrideReason) payload.balance_override_reason = overrideReason;
      await post('/job-orders/'+jobId+'/dispatch', payload);
      setShowDispatch(false); setBalanceWarning(null);
      loadJob(); onUpdated();
    } catch (err) {
      if (err.pending_approval) {
        alert(err.message);
        setShowDispatch(false);
        setBalanceWarning(null);
      } else if (err.requires_override) {
        setBalanceWarning(err);
      } else {
        setDispatchError(err.message);
      }
    } finally { setSubmitting(false); }
  };

  const handleDeliver = async () => {
    setSubmitting(true);
    try {
      await post('/job-orders/'+jobId+'/deliver', deliverForm);
      setShowDeliver(false); loadJob(); onUpdated();
    } catch (err) { alert(err.message); } finally { setSubmitting(false); }
  };
  const modalPermissions =
    Array.isArray(user?.permissions)
      ? user.permissions
      : [];

  const isSalesWriter =
    modalPermissions.includes(
      'invoices:write'
    ) ||
    user?.role ===
      'field_sales_representative' ||
    [
      'customer_experience_officer',
      'brand_content_officer'
    ].includes(user?.role);

  const canEdit =
    ['super_admin','sales_manager'].includes(
      user?.role
    ) ||
    (
      isSalesWriter &&
      String(job?.sales_rep_id || '') ===
        String(user?.id || '')
    );

  const canUploadMockup = canEdit;

  const pendingMockupRequest =
    mockupRequestState?.request?.status ===
      'pending'
      ? mockupRequestState.request
      : null;

  const load = () =>
    get('/job-orders/'+jobId)
      .then(setJob);

  const loadMockups = () =>
    get('/job-orders/'+jobId+'/mockups')
      .then(d => setMockups(d.data||[]));

  const loadMockupRequest = () =>
    get(
      '/job-orders/' +
      jobId +
      '/mockup-request'
    ).then(setMockupRequestState);

  const requestMockup = async () => {
    const note = window.prompt(
      'Describe the missing mockup required for production:'
    );

    if (note === null) return;

    if (note.trim().length < 5) {
      alert(
        'Please describe the required mockup in at least five characters.'
      );
      return;
    }

    setRequestingMockup(true);

    try {
      await post(
        '/job-orders/' +
        jobId +
        '/mockup-request',
        {
          request_note: note.trim()
        }
      );

      await loadMockupRequest();

      alert(
        'The responsible sales representative has been notified.'
      );
    } catch (error) {
      alert(
        error.message ||
        'The mockup request could not be sent.'
      );
    } finally {
      setRequestingMockup(false);
    }
  };

  const cancelMockupRequest = async () => {
    const reason = window.prompt(
      'Why is this mockup request being cancelled?'
    );

    if (reason === null) return;

    if (reason.trim().length < 5) {
      alert(
        'Enter a cancellation reason of at least five characters.'
      );
      return;
    }

    setRequestingMockup(true);

    try {
      await post(
        '/job-orders/' +
        jobId +
        '/mockup-request/cancel',
        {
          cancellation_reason:
            reason.trim()
        }
      );

      await loadMockupRequest();
    } catch (error) {
      alert(
        error.message ||
        'The request could not be cancelled.'
      );
    } finally {
      setRequestingMockup(false);
    }
  };

  useEffect(() => {
    load();
    loadMockups();
    loadMockupRequest();
  }, [jobId]);


  const handleMockupUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('mockup', file);
    try {
      const token = localStorage.getItem('bb_token');
      const res = await fetch('/api/job-orders/'+jobId+'/mockups', {
        method: 'POST',
        headers: { 'Authorization': 'Bearer '+token },
        body: formData
      });
      if (!res.ok) {
        const failure =
          await res.json().catch(() => ({}));

        throw new Error(
          failure.error ||
          'Upload failed'
        );
      }

      await loadMockups();
      await loadMockupRequest();
    } catch (err) {
      alert(err.message);
    } finally {
      setUploading(false);
    }
  };

  const viewMockup = async mockup => {
    const popup =
      window.open('', '_blank');

    if (!popup) {
      window.alert(
        'The mockup window was blocked. Allow pop-ups and try again.'
      );
      return;
    }

    popup.document.write(
      '<p style="font-family:Arial;padding:24px">Loading mockup...</p>'
    );

    try {
      const token =
        localStorage.getItem(
          'bb_token'
        );

      const response = await fetch(
        '/api/job-orders/' +
          jobId +
          '/mockups/' +
          mockup.id +
          '/file',
        {
          headers: {
            Authorization:
              'Bearer ' + token
          }
        }
      );

      if (!response.ok) {
        throw new Error(
          'Mockup could not be opened'
        );
      }

      const blob =
        await response.blob();

      const objectUrl =
        URL.createObjectURL(blob);

      popup.location.replace(
        objectUrl
      );

      window.setTimeout(
        () => {
          URL.revokeObjectURL(
            objectUrl
          );
        },
        300000
      );
    } catch (error) {
      popup.close();

      window.alert(
        error.message
      );
    }
  };

  const deleteMockup = async (mockupId) => {
    if (!window.confirm('Remove this mockup?')) return;
    const token = localStorage.getItem('bb_token');
    await fetch('/api/job-orders/'+jobId+'/mockups/'+mockupId, {
      method: 'DELETE',
      headers: { 'Authorization': 'Bearer '+token }
    });
    loadMockups();
  };

  if (!job) return (
    <div className="modal-overlay">
      <div className="modal" style={{textAlign:'center',padding:40}}>
        <div className="spinner"></div>
      </div>
    </div>
  );

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:700}}>
        <div className="modal-hdr">
          <div>
            <span className="modal-title">{job.job_number}</span>
            <span style={{marginLeft:10}}><StatusBadge status={job.status} /></span>
          </div>
          <div className="flex-center gap-sm">
            {canEdit && job.status !== 'cancelled' && job.status !== 'dispatched' && (
              <button className="btn btn-outline btn-sm" onClick={() => setShowEdit(true)}>Edit</button>
            )}
            <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
          </div>
        </div>
        <div className="modal-body">
          <OrderSpecificationReview jobId={jobId} onChanged={load} />
          {/* Tabs */}
          <div style={{display:'flex',gap:6,marginBottom:16,borderBottom:'1px solid var(--border-light)',paddingBottom:12}}>
            {['details','materials','costing','mockups','activity'].map(tab => (
              <button key={tab} className={'btn btn-sm '+(activeTab===tab?'btn-primary':'btn-ghost')}
                style={{textTransform:'capitalize'}} onClick={()=>setActiveTab(tab)}>
                {tab==='materials'?'Materials':tab==='costing'?'Job Costing':tab.charAt(0).toUpperCase()+tab.slice(1)}
              </button>
            ))}
          </div>

          {activeTab==='details' && <div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16,padding:14,background:'#F2EFE7',borderRadius:8}}>
            <div>
              <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:4}}>Customer</div>
              <div style={{fontWeight:600,fontSize:14,color:'#3B2A21'}}>{job.customer_name}</div>
              {['super_admin','admin','sales_manager','content_sales'].includes(user?.role) && (
                <>
                  {job.customer_phone && <div style={{fontSize:12,color:'#555',marginTop:2}}>📞 {job.customer_phone}</div>}
                  {job.customer_email && <div style={{fontSize:12,color:'#555',marginTop:2}}>✉ {job.customer_email}</div>}
                </>
              )}
            </div>
            <div>
              <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:4}}>Invoice</div>
              <div style={{fontWeight:600,fontSize:14,color:'#3B2A21'}}>{job.invoice_number}</div>
              <div style={{fontSize:12,color:'#555',marginTop:2}}>Job Date: {job.date}</div>
              {job.delivery_date && <div style={{fontSize:12,color:'#8F2E24',fontWeight:600,marginTop:2}}>Due: {job.delivery_date}</div>}
            </div>
          </div>

          {/* Contact & Delivery — Restricted */}
          {['super_admin','admin','sales_manager','content_sales'].includes(user?.role) &&
           (job.customer_address || job.delivery_instructions) && (
            <div style={{background:'#FFF3DC',border:'1px solid #F5C842',borderRadius:8,
              padding:14,marginBottom:16}}>
              <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',
                color:'#8B5E0A',marginBottom:10}}>🔒 Contact & Delivery Details</div>
              {job.customer_address && (
                <div style={{marginBottom:8}}>
                  <div style={{fontSize:10,color:'#8B5E0A',fontWeight:600,marginBottom:2}}>Delivery Address</div>
                  <div style={{fontSize:12,color:'#3B2A21'}}>{job.customer_address}</div>
                </div>
              )}
              {job.delivery_instructions && (
                <div>
                  <div style={{fontSize:10,color:'#8B5E0A',fontWeight:600,marginBottom:2}}>Delivery Instructions</div>
                  <div style={{fontSize:12,color:'#3B2A21'}}>{job.delivery_instructions}</div>
                </div>
              )}
            </div>
          )}

          <div style={{marginBottom:16}}>
            <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:10}}>Product Specifications</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10}}>
              {[['Product',job.product_description],['Size',job.size],
                ['Quantity',job.quantity?Number(job.quantity).toLocaleString():null],
                ['Material',job.material],['Color',job.color],['Handle',job.handle_type]]
                .filter(([,v]) => v).map(([label,value]) => (
                <div key={label} style={{padding:'10px 12px',background:'#fff',border:'1px solid var(--border-light)',borderRadius:6}}>
                  <div style={{fontSize:9,color:'#8A8A8A',fontWeight:600,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:3}}>{label}</div>
                  <div style={{fontSize:13,color:'#3B2A21',fontWeight:500}}>{value}</div>
                </div>
              ))}
            </div>
          </div>

          {job.print_description && (
            <div style={{marginBottom:12,padding:'12px 14px',background:'#F2EFE7',borderRadius:8}}>
              <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:6}}>Print Description</div>
              <div style={{fontSize:13,color:'#3B2A21',lineHeight:1.6}}>{job.print_description}</div>
            </div>
          )}

          {job.special_instructions && (
            <div style={{marginBottom:12,padding:'12px 14px',background:'#FFF3DC',borderRadius:8,border:'1px solid #F0C860'}}>
              <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8B5E0A',marginBottom:6}}>Special Instructions</div>
              <div style={{fontSize:13,color:'#3B2A21',lineHeight:1.6}}>{job.special_instructions}</div>
            </div>
          )}

          {job.notes && (
            <div style={{marginBottom:16,padding:'10px 12px',background:'#fff',border:'1px solid var(--border-light)',borderRadius:8}}>
              <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:4}}>Notes</div>
              <div style={{fontSize:12,color:'#555'}}>{job.notes}</div>
            </div>
          )}

          {canDispatch && job.status === 'ready' && (
            <button className="btn btn-primary btn-sm" style={{background:'#1E4FAD'}}
              onClick={() => { setShowDispatch(true); setDispatchForm({...dispatchForm, delivery_address: job.customer_address||''}); }}>
              🚚 Dispatch Order
            </button>
          )}
          {canDispatch && job.status === 'dispatched' && (
            <button className="btn btn-primary btn-sm" style={{background:'#2E6B3A'}}
              onClick={() => setShowDeliver(true)}>
              ✓ Confirm Delivery
            </button>
          )}

          {canEdit && job.status !== 'cancelled' && job.status !== 'dispatched' && (
            <div style={{display:'flex',gap:10,marginBottom:16}}>
              <button className="btn btn-outline btn-sm" style={{color:'#8F2E24',borderColor:'#8F2E24'}}
                onClick={async () => {
                  const reason = window.prompt('Reason for cancellation?');
                  if (!reason) return;
                  await put('/job-orders/'+jobId+'/status', { status:'cancelled', note:'CANCELLED: '+reason });
                  load();
                }}>
                Cancel Job Order
              </button>
              {user?.role === 'super_admin' && (
                <button className="btn btn-danger btn-sm"
                  onClick={async () => {
                    if (!window.confirm('Permanently delete this job order? This cannot be undone.')) return;
                    const token = localStorage.getItem('bb_token');
                    await fetch('/api/job-orders/'+jobId, { method:'DELETE', headers:{'Authorization':'Bearer '+token} });
                    onUpdated();
                  }}>
                  Delete Permanently
                </button>
              )}
            </div>
          )}

          <div style={{marginBottom:16}}>
            <div style={{
              display:'flex',
              alignItems:'center',
              justifyContent:'space-between',
              gap:10,
              marginBottom:10
            }}>
              <div style={{
                fontSize:9,
                fontWeight:600,
                letterSpacing:'.1em',
                textTransform:'uppercase',
                color:'#8A8A8A'
              }}>
                Design Mockups
              </div>

              <div style={{
                display:'flex',
                gap:8,
                flexWrap:'wrap',
                justifyContent:'flex-end'
              }}>
                {canRequestMockup &&
                 mockups.length === 0 &&
                 !pendingMockupRequest && (
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    disabled={requestingMockup}
                    onClick={requestMockup}
                  >
                    {requestingMockup
                      ? 'Sending...'
                      : 'Request Mockup'}
                  </button>
                )}

                {canRequestMockup &&
                 pendingMockupRequest && (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    disabled={requestingMockup}
                    onClick={cancelMockupRequest}
                    style={{color:'#8F2E24'}}
                  >
                    Cancel Request
                  </button>
                )}

                {canUploadMockup && (
                  <label style={{
                    display:'inline-flex',
                    alignItems:'center',
                    gap:6,
                    padding:'6px 12px',
                    background:'#556B2F',
                    color:'#F2EFE7',
                    borderRadius:7,
                    fontSize:12,
                    fontWeight:500,
                    cursor:
                      uploading
                        ? 'not-allowed'
                        : 'pointer',
                    opacity:
                      uploading
                        ? 0.65
                        : 1
                  }}>
                    {uploading
                      ? 'Uploading...'
                      : '+ Upload Mockup'}

                    <input
                      type="file"
                      accept="image/*,.pdf"
                      style={{display:'none'}}
                      onChange={handleMockupUpload}
                      disabled={uploading}
                    />
                  </label>
                )}
              </div>
            </div>

            {pendingMockupRequest && (
              <div style={{
                padding:'12px 14px',
                marginBottom:10,
                borderRadius:8,
                background:'#FFF3DC',
                border:'1px solid #A8893D55',
                fontSize:12,
                color:'#6E4A08'
              }}>
                <div style={{
                  fontWeight:700,
                  marginBottom:4
                }}>
                  Mockup requested from{' '}
                  {pendingMockupRequest
                    .assigned_to_name}
                </div>

                <div>
                  {pendingMockupRequest
                    .request_note}
                </div>
              </div>
            )}

            {mockups.length === 0
              ? <div style={{padding:'20px',textAlign:'center',background:'#F2EFE7',borderRadius:8,fontSize:12,color:'#8A8A8A'}}>No mockups uploaded yet.</div>
              : <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))',gap:10}}>
                  {mockups.map(m => (
                    <div key={m.id} style={{border:'1px solid var(--border-light)',borderRadius:8,overflow:'hidden',background:'#fff'}}>
                      {m.filename.match(/\.(jpg|jpeg|png|gif|webp)$/i)
                        ? <SecureMockupImage
                              jobId={jobId}
                              mockup={m}
                            />
                        : <div style={{width:'100%',height:120,display:'flex',alignItems:'center',justifyContent:'center',background:'#F2EFE7',fontSize:28}}>pdf</div>
                      }
                      <div style={{padding:'8px 10px'}}>
                        <div style={{fontSize:11,color:'#3B2A21',fontWeight:500,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{m.original_name}</div>
                        <div style={{fontSize:10,color:'#8A8A8A',marginTop:2}}>{m.uploaded_by_name}</div>
                        <div style={{display:'flex',gap:6,marginTop:6}}>
                          <button
                              type="button"
                              onClick={() => viewMockup(m)}
                              style={{fontSize:11,color:'#556B2F',fontWeight:500,background:'none',border:'none',cursor:'pointer',padding:0}}
                            >
                              View
                            </button>
                          <span style={{color:'#DDD4C0'}}>|</span>
                          <button onClick={() => deleteMockup(m.id)} style={{fontSize:11,color:'#8F2E24',fontWeight:500,background:'none',border:'none',cursor:'pointer',padding:0}}>Remove</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
            }
          </div>

          </div>}

          {/* Dispatch Info */}
          {activeTab==='details' && job.logistics_company && (
            <div style={{marginTop:12,background:'#EDF4FF',borderRadius:8,padding:'12px 16px',border:'1px solid #C3D9FF'}}>
              <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#1E4FAD',marginBottom:8}}>Dispatch Details</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,fontSize:12}}>
                <div><span style={{color:'#8A8A8A'}}>Logistics: </span><strong>{job.logistics_company}</strong></div>
                <div><span style={{color:'#8A8A8A'}}>Driver: </span><strong>{job.driver_name}</strong></div>
                {job.driver_phone && <div><span style={{color:'#8A8A8A'}}>Phone: </span>{job.driver_phone}</div>}
                {job.tracking_number && <div><span style={{color:'#8A8A8A'}}>Tracking: </span><strong>{job.tracking_number}</strong></div>}
                {job.dispatch_date && <div><span style={{color:'#8A8A8A'}}>Dispatched: </span>{new Date(job.dispatch_date).toLocaleDateString('en-NG')}</div>}
                {job.actual_delivery_date && <div><span style={{color:'#2E6B3A'}}>Delivered: </span><strong style={{color:'#2E6B3A'}}>{job.actual_delivery_date}</strong></div>}
                {job.delivery_address && <div style={{gridColumn:'1/-1'}}><span style={{color:'#8A8A8A'}}>Address: </span>{job.delivery_address}</div>}
                {job.balance_override_reason && <div style={{gridColumn:'1/-1',color:'#8F2E24'}}><span style={{color:'#8A8A8A'}}>Balance Override: </span>{job.balance_override_reason}</div>}
              </div>
            </div>
          )}

          <OpenProductionTasksWarning
            jobOrderId={jobId}
            jobOrderStatus={job.status}
            compact
          />

          {activeTab==='materials' && (
            <MaterialIssuance jobId={jobId} jobNumber={job.job_number} />
          )}

          {activeTab==='costing' && (
            <JobCostingSummary jobId={jobId} />
          )}

          {activeTab==='mockups' && (
            <div>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
                <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A'}}>Design Mockups</div>
                <label style={{display:'inline-flex',alignItems:'center',gap:6,padding:'6px 12px',background:'#556B2F',color:'#F2EFE7',borderRadius:7,fontSize:12,fontWeight:500,cursor:'pointer'}}>
                  {uploading ? 'Uploading...' : '+ Upload Mockup'}
                  <input type="file" accept="image/*,.pdf" style={{display:'none'}} onChange={handleMockupUpload} disabled={uploading} />
                </label>
              </div>
              {mockups.length === 0
                ? <div style={{padding:'20px',textAlign:'center',background:'#F2EFE7',borderRadius:8,fontSize:12,color:'#8A8A8A'}}>No mockups uploaded yet.</div>
                : <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))',gap:10}}>
                    {mockups.map(m => (
                      <div key={m.id} style={{border:'1px solid var(--border-light)',borderRadius:8,overflow:'hidden',background:'#fff'}}>
                        {m.filename.match(/\.(jpg|jpeg|png|gif|webp)$/i)
                          ? <SecureMockupImage
                              jobId={jobId}
                              mockup={m}
                            />
                          : <div style={{width:'100%',height:120,display:'flex',alignItems:'center',justifyContent:'center',background:'#F2EFE7',fontSize:28}}>📄</div>
                        }
                        <div style={{padding:'8px 10px'}}>
                          <div style={{fontSize:11,color:'#3B2A21',fontWeight:500,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{m.original_name}</div>
                          <div style={{fontSize:10,color:'#8A8A8A',marginTop:2}}>{m.uploaded_by_name}</div>
                          <div style={{display:'flex',gap:6,marginTop:6}}>
                            <button
                              type="button"
                              onClick={() => viewMockup(m)}
                              style={{fontSize:11,color:'#556B2F',fontWeight:500,background:'none',border:'none',cursor:'pointer',padding:0}}
                            >
                              View
                            </button>
                            <span style={{color:'#DDD4C0'}}>|</span>
                            <button onClick={() => deleteMockup(m.id)}
                              style={{fontSize:11,color:'#8F2E24',fontWeight:500,background:'none',border:'none',cursor:'pointer',padding:0}}>Remove</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
              }
            </div>
          )}

          {activeTab==='activity' && (
            <div>
              <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:10}}>Activity Log</div>
              <div style={{position:'relative',paddingLeft:20}}>
                <div style={{position:'absolute',left:6,top:0,bottom:0,width:2,background:'var(--border-light)'}}></div>
                {job.updates.map(u => (
                  <div key={u.id} style={{position:'relative',marginBottom:12,paddingLeft:16}}>
                    <div style={{position:'absolute',left:-14,top:4,width:8,height:8,borderRadius:'50%',background:getStatus(u.status).color,border:'2px solid #fff',boxShadow:'0 0 0 1px var(--border-light)'}}></div>
                    <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:2}}>
                      <StatusBadge status={u.status} />
                      <span style={{fontSize:11,color:'#8A8A8A'}}>{new Date(u.created_at).toLocaleString('en-NG')}</span>
                      <span style={{fontSize:11,color:'#555'}}>by {u.updated_by_name}</span>
                    </div>
                    {u.note && <div style={{fontSize:12,color:'#555',marginTop:2}}>{u.note}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      {showEdit && (
        <EditJobModal job={job} onClose={() => setShowEdit(false)} onSaved={() => { setShowEdit(false); load(); }} />
      )}
    </div>
  );
}
